import React, { useEffect, useRef, useState } from "react";
import { AddFlex, CustomText, Gap } from "../reusableStyles";
import styled from "styled-components";
import {
  ArrowRight,
  ArrowUp,
  ChevronRight,
  HelpCircle,
  Home,
  Settings,
  User,
} from "lucide-react";
import { Add } from "@mui/icons-material";
import growezyAi from "../ImageAssets/growezyArrow.svg";
import googleCalendar from "../ImageAssets/google_calendar.svg";
import insta_logo from "../ImageAssets/insta_logo.png";

import TextareaAutosize from "@mui/material/TextareaAutosize";
import { useSelector } from "react-redux";
import {
  GetBusinessChatHistoryApi,
  GoogleCalendarIntegrationApi,
  InstagramIntegrationApi,
  UnderstandBusinessApi,
  UploadBusinessFile,
} from "../Api/ApiEndPoints";
import { callNonStoreApi } from "../NonStoreApis";
import { FileComponent } from "../Integrations/Instagram/AiInstaDmAutomation";
import { CircularProgress, Divider } from "@mui/material";
import { isArrayNullOrUndefined, isStringNullOrUndefined } from "../utils";
import { Button } from "antd";
import useIntegrationHooks from "../CustomHooks/useIntegrationHooks";
import { enqueueSnackbar } from "notistack";

const Input = styled(TextareaAutosize)`
  width: 100%;
  background-color: transparent;
  border: none;
  outline: none;
  color: black;
  font-family: outfit;
  resize: none;
  ::placeholder: {
    color: #73726c;
  }
`;

const HeaderText = styled.h1`
  color: #004ace;
  font-size: 2rem;
  font-family: serif;
`;

function ChatMain() {
  const { apiUserData } = useSelector((state) => state.user);
  const { credentials } = useSelector((state) => state.instagram);
  const [messages, setMessages] = useState();
  const [input, setInput] = useState("");
  const [file, setFile] = useState(null);
  const [fileTextId, setFileTextId] = useState(false);
  const [isFileUploading, setIsFileUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [generatingBusinessSummary, setGeneratingBusinessSUmmary] =
    useState(false);
  const [messageLoading, setMessageLoading] = useState(false);
  const [aiMessage, setAiMessage] = useState("");
  const fileUploadRef = useRef(null);

  const getPreviousConversation = async () => {
    const response = await callNonStoreApi(GetBusinessChatHistoryApi, {
      instaUserId: credentials?.user_id,
    });
    const { userBusinessChatHistory } = response.data;
    setMessages(userBusinessChatHistory || []);
  };

  const sendMessage = async () => {
    try {
      if (!input.trim()) return;
      setMessageLoading(true);

      const userMessage = { content: input, role: "user", message: input };
      if (file) {
        userMessage.file = {
          fileName: file.name,
          type: file.type,
        };
      }
      setInput("");
      setFile(null);
      // make api call.
      setMessages([...messages, userMessage]);
      const response = await callNonStoreApi(UnderstandBusinessApi, {
        message: userMessage.content,
        instaUserId: credentials?.user_id,
        fileTextId,
        file,
      });
      const { aiResponse, UIElements } = response.data;
      setInput("");
      setMessageLoading(false);
      setLoading(true);
      setAiMessage("");

      // Simulating AI response with typing effect
      const fullResponse = aiResponse;
      const aiMessage = {
        content: fullResponse,
        role: "assistant",
        UIElements,
      };
      setMessages((prev) => [...prev, aiMessage]);
    } catch (error) {
      console.log(error);
    } finally {
      setMessageLoading(false);
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    // setLoading(true);
    try {
      setIsFileUploading(true);
      const file = e.target.files[0];
      setFile(file);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("instaUserId", credentials?.user_id);
      const response = await callNonStoreApi(UploadBusinessFile, formData);
      const { id } = response.data;
      setFileTextId(id);
    } catch (error) {
      console.error("Error uploading file", error);
    } finally {
      setIsFileUploading(false);
    }
    // setLoading(false);
  };

  useEffect(() => {
    getPreviousConversation();
  }, []);

  return (
    <AddFlex backgroundColor="white" height="100%" style={{ flexGrow: 1 }}>
      {/* <ChatSidebar /> */}
      <AddFlex
        justify="center"
        alignItems="center"
        flexDirection="column"
        gap="30px"
        style={{ width: "100%", height: "100%" }}
      >
        {isArrayNullOrUndefined(messages) ? (
          <AddFlex flexDirection="column" gap="30px" width="50%">
            <ChatHeader
              text={`Hello ${apiUserData.Name}, let's discuss your business.`}
            />
            <ChatInput
              setInput={setInput}
              input={input}
              loading={loading}
              sendMessage={sendMessage}
              messageLoading={messageLoading}
              handleUpload={handleUpload}
            />
          </AddFlex>
        ) : (
          <ChatUIDefault
            handleUpload={handleUpload}
            sendMessage={sendMessage}
            messages={messages}
            messageLoading={messageLoading}
            input={input}
            loading={loading}
            setInput={setInput}
          />
        )}
      </AddFlex>
    </AddFlex>
  );
}

export default ChatMain;

export const ChatHeader = ({ text }) => {
  return (
    <AddFlex flexDirection="column" alignItems="center" justify="center">
      <HeaderText>{text}</HeaderText>
    </AddFlex>
  );
};

export const ChatUIDefault = ({
  messages,
  sendMessage,
  input,
  setInput,
  handleUpload,
  messageLoading,
}) => {
  return (
    <>
      <AddFlex
        flexDirection="column"
        width="50%"
        padding="20px 0 20px 0"
        height="100%"
      >
        <ChatMessages messages={messages} messageLoading={messageLoading} />
        <ChatInput
          messageLoading={messageLoading}
          input={input}
          setInput={setInput}
          sendMessage={sendMessage}
          handleUpload={handleUpload}
        />
      </AddFlex>
    </>
  );
};

export const ChatMessages = ({ messages, messageLoading }) => {
  const { apiUserData } = useSelector((state) => state.user);

  return (
    <AddFlex
      flexDirection="column"
      width="100%"
      grow={1}
      style={{ overflow: "scroll" }}
    >
      {messages.map((msg, index) => (
        <>
          <AddFlex gap="10px" flexDirection="column">
            <div
              key={index}
              transition={{ duration: 0.3 }}
              className={`p-3 flex gap-2 items-start whitespace-pre-line ${
                msg.role === "user"
                  ? `bg-[#d5e1f7] w-max rounded-lg pl-3 pr-3`
                  : "bg-transaprent"
              }`}
              style={{ maxWidth: "100%" }}
            >
              {msg.role === "user" && (
                <AddFlex
                  width="20px"
                  height="20px"
                  backgroundColor={"white"}
                  color="black"
                  borderRadius="50%"
                  justify="center"
                  alignItems="center"
                  style={{
                    fontWeight: "800",
                    fontSize: "12px",
                    flexShrink: 0,
                    marginTop: "2px",
                  }}
                >
                  {apiUserData.Name.slice(0, 1)}
                </AddFlex>
              )}
              {/* {msg.file && (
        <FileComponent width="100%" isFileUploading={false} file={msg.file} />
      )} */}
              <div
                className={`whitespace-pre-line text-[#141413] `}
                dangerouslySetInnerHTML={{
                  __html:
                    msg.role === "user"
                      ? msg.message.replace(
                          /\*\*(.*?)\*\*/g,
                          "<strong>$1</strong>"
                        )
                      : msg.content.replace(
                          /\*\*(.*?)\*\*/g,
                          "<strong>$1</strong>"
                        ),
                }}
                style={
                  msg.role !== "user"
                    ? {
                        fontFamily: "Inter",
                        fontSize: "15px",
                        lineHeight: "24px",
                      }
                    : { fontSize: "15px", lineHeight: "24px" }
                }
              ></div>
            </div>
            {!isArrayNullOrUndefined(msg.UIElements) && (
              <AddFlex margin="10px 0">
                {msg.UIElements.map((elem) => (
                  <UIElementComponent elem={elem} key={elem.id} />
                ))}
              </AddFlex>
            )}
          </AddFlex>{" "}
          {index % 2 !== 0 && (
            <Divider style={{ margin: "30px 0", backgroundColor: "#F0EEE6" }} />
          )}
        </>
      ))}
      {messageLoading && (
        <AddFlex
          alignItems="center"
          className="p-1 mt-1 whitespace-pre-line text-black"
          gap="10px"
        >
          {" "}
          <CircularProgress size={12} sx={{ color: "white" }} /> Please Wait...{" "}
        </AddFlex>
      )}
    </AddFlex>
  );
};

export const ChatInput = ({
  sendMessage,
  messageLoading,
  input,
  setInput,
  loading,
  handleUpload,
}) => {
  return (
    <form
      style={{ width: "100%" }}
      onSubmit={(e) => {
        e.preventDefault();
        if (isStringNullOrUndefined(input) || messageLoading) {
          return;
        }
        sendMessage();
      }}
    >
      <AddFlex
        padding="15px"
        backgroundColor="white"
        borderRadius="18px"
        border="1px solid #DDDDDD"
        flexDirection="column"
        gap="25px"
      >
        <Input
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
          }}
          placeholder="Tell me about your business.."
        />
        <AddFlex>
          <AddFlex
            width="30px"
            height="30px"
            borderRadius="50%"
            justify="center"
            alignItems="center"
            style={{ cursor: "pointer", border: "1px solid #DDDDDD" }}
          >
            <Add style={{ color: "#73726C", width: "15px" }} />
          </AddFlex>
          <Gap />
          <AddFlex
            backgroundColor={
              isStringNullOrUndefined(input) || messageLoading
                ? "grey"
                : "#004ACE"
            }
            width="30px"
            height="30px"
            borderRadius="50%"
            justify="center"
            alignItems="center"
            style={{ cursor: "pointer" }}
            onClick={() => {
              if (isStringNullOrUndefined(input) || messageLoading) {
                return;
              }
              sendMessage();
            }}
          >
            {loading ? (
              <CircularProgress size={15} sx={{ color: "white" }} />
            ) : (
              <ArrowUp style={{ color: "white", width: "15px" }} />
            )}
          </AddFlex>
        </AddFlex>
      </AddFlex>
    </form>
  );
};

const getIntegrationName = (elem) => {
  switch (elem.id) {
    case "connect_google_calendar":
      return "google_calendar";
    case "start_instagram_integration":
      return "instagram_connect";
  }
};

export const UIElementComponent = ({ elem }) => {
  // Write use effect to initiate action immediately.
  const integrationName = getIntegrationName(elem);
  const { apiUserData } = useSelector((state) => state.user);
  const { apiLoading, isIntegrationActivated } = useIntegrationHooks(
    integrationName,
    "",
    ""
  );
  const [loading, setLoading] = useState(false);
  const handleElementOnClick = () => {
    setLoading(true);
    switch (elem.id) {
      case "connect_google_calendar":
        if (isIntegrationActivated()) return;
        callNonStoreApi(GoogleCalendarIntegrationApi, null, {
          params: {
            username: apiUserData.userName,
          },
        }).then((response) => {
          window.location.href = response.data.url;
        });
        break;
      case "start_instagram_integration":
        if (isIntegrationActivated()) return;
        setLoading(true);
        callNonStoreApi(InstagramIntegrationApi, null, {
          params: {
            username: apiUserData.userName,
            source: "chatbot",
          },
        })
          .then((response) => {
            window.location.href = response.data.instagramAuthUrl;
          })
          .catch((error) => {
            console.error(error);
            enqueueSnackbar("There is some error in connecting to Instagram", {
              variant: "error",
            });
            setLoading(false);
          });

        return;
    }
  };
  const getImage = () => {
    switch (elem.id) {
      case "connect_google_calendar":
        return googleCalendar;
      case "start_instagram_integration":
        return insta_logo;
    }
  };
  useEffect(() => {
    handleElementOnClick();
  }, []);
  return (
    <AddFlex
      key={elem.id}
      gap="5px"
      className="rounded-full"
      padding="8px 12px"
      //   borderRadius="12px"
      alignItems="center"
      backgroundColor="#F0EEE6"
      style={{ cursor: "pointer" }}
      onClick={handleElementOnClick}
    >
      <img src={getImage()} style={{ width: "15px", height: "15px" }} />
      {/* {isIntegrationActivated() && (
          <CustomText fontSize="14px" fontWeight="700" color="green">
            Connected
          </CustomText>
        )}
       */}
      <CustomText color="#141413" fontSize="14px" fontWeight="500">
        {elem.name}
      </CustomText>
      <ArrowRight style={{ color: "#141413", width: "14px" }} />
    </AddFlex>
  );
};
